import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import SearchOutlinedIcon from '@mui/icons-material/SearchOutlined';
import TuneOutlinedIcon from '@mui/icons-material/TuneOutlined';
import ClientLogoStrip from './ClientLogoStrip';
import { MotionButton, FadeInSection } from '../ui/MotionPrimitives';

const HomeHero: React.FC = () => {
  const navigate = useNavigate();
  const [listingType, setListingType] = useState<'all' | 'sale' | 'rent'>('all');
  const [propertyType, setPropertyType] = useState<
    'all' | 'apartments' | 'villas' | 'plots' | 'hotels' | 'projects_offplan'
  >('all');
  const [locationQuery, setLocationQuery] = useState('');
  const [minPrice, setMinPrice] = useState('');
  const [maxPrice, setMaxPrice] = useState('');

  const handleSearch = () => {
    const params = new URLSearchParams();
    if (listingType !== 'all') {
      params.set('listingType', listingType);
    }
    if (propertyType !== 'all') {
      params.set('propertyType', propertyType);
    }
    if (locationQuery.trim()) {
      params.set('location', locationQuery.trim());
    }
    if (minPrice.trim()) {
      params.set('minPrice', minPrice.trim());
    }
    if (maxPrice.trim()) {
      params.set('maxPrice', maxPrice.trim());
    }

    const query = params.toString();
    navigate(`/properties${query ? `?${query}` : ''}`);
  };

  return (
    <FadeInSection className="w-full text-white">
      <div
        className="w-full min-h-[85vh] overflow-hidden bg-cover bg-center"
        style={{ backgroundImage: "url('/images/pics/pexels-binyaminmellish-186077.jpg')" }}
      >
        <div className="relative bg-gradient-to-b from-black/30 via-black/40 to-black/80 pt-24 pb-10 md:pt-28 md:pb-16 lg:pt-32 lg:pb-20">
          <div className="mx-auto flex max-w-6xl flex-col px-6 py-10 md:px-10 md:py-16 lg:py-20">
            {/* Main content layout */}
            <div className="mt-[40px] flex flex-col gap-10 md:flex-row md:items-end md:justify-between">
              {/* Left: Heading, description, primary CTAs */}
              <div className="max-w-4xl md:max-w-xl">
                {/* Main heading */}
                <div className="mb-3 md:mb-5">
                  <h1 className="text-[26px] font-semibold leading-tight md:text-[32px] lg:text-[40px]">
                    Strategic Global Real Estate.
                  </h1>
                  <h2 className="text-[26px] font-semibold leading-tight text-[#D88546] md:text-[32px] lg:text-[40px]">
                    Impact in USA &amp; UAE
                  </h2>
                </div>

                {/* Description */}
                <p className="mb-5 max-w-xl text-xs leading-relaxed text-white/90 md:text-sm">
                  Hogar Dorado House connects the stability of the
                  <span className="font-semibold"> USA</span> with the high-growth potential of the
                  <span className="font-semibold"> UAE</span> through focused real estate investment
                  &amp; advisory.
                </p>

                {/* Primary CTAs */}
                <div className="mb-4 flex flex-wrap gap-4 md:mb-0">
                  <MotionButton className="rounded-[6px] bg-white px-6 py-2 text-sm font-semibold text-black shadow-sm transition-colors hover:bg-white/90 md:px-7 md:py-2.5">
                    Explore Investment Opportunities
                  </MotionButton>
                  <MotionButton className="rounded-[6px] bg-[#D88546] px-6 py-2 text-sm font-semibold text-black shadow-sm transition-colors hover:bg-[#e59252] md:px-7 md:py-2.5">
                    Book A Consultation
                  </MotionButton>
                </div>
              </div>

              {/* Right: Search card - overlaps bottom of hero */}
              <div className="pointer-events-auto md:flex-none transform -translate-y-4 md:-translate-y-5">
                <div className="relative -mb-20 w-full shrink-0 rounded-tr-2xl rounded-br-2xl rounded-bl-2xl bg-white px-4 pb-5 pt-8 text-black shadow-xl md:-mb-24 md:w-[380px] md:px-5">
                  {/* Tabs: All / For Sale / For Rent */}
                  <div className="absolute -top-[35px] left-0 flex overflow-hidden rounded-tl-2xl rounded-tr-2xl border-b border-black/10 bg-white text-[11px] font-medium md:text-xs">
                    <button
                      type="button"
                      onClick={() => setListingType('all')}
                      className={`px-4 py-1.5 ${
                        listingType === 'all' ? 'text-black' : 'text-black/60'
                      }`}
                    >
                      <div className="flex flex-col items-center">
                        <span>All</span>
                        {listingType === 'all' && (
                          <span className="mt-1 h-[2px] w-full bg-black" />
                        )}
                      </div>
                    </button>
                    <button
                      type="button"
                      onClick={() => setListingType('sale')}
                      className={`px-4 py-1.5 ${
                        listingType === 'sale' ? 'text-black' : 'text-black/60'
                      }`}
                    >
                      For Sale
                    </button>
                    <button
                      type="button"
                      onClick={() => setListingType('rent')}
                      className={`px-4 py-1.5 ${
                        listingType === 'rent' ? 'text-black' : 'text-black/60'
                      }`}
                    >
                      For Rent
                    </button>
                  </div>

                  <div className="space-y-4">
                    {/* Property Type */}
                    <div className="flex w-full items-center justify-between rounded-2xl border border-black/5 bg-white px-3 py-2.5 text-xs text-black/70 md:text-sm">
                      <span>Property Type</span>
                      <select
                        className="ml-3 flex-1 bg-transparent text-right text-xs text-black/80 outline-none md:text-sm"
                        value={propertyType}
                        onChange={(e) =>
                          setPropertyType(e.target.value as
                            | 'all'
                            | 'apartments'
                            | 'villas'
                            | 'plots'
                            | 'hotels'
                            | 'projects_offplan')
                        }
                      >
                        <option value="all">All</option>
                        <option value="apartments">Apartments</option>
                        <option value="villas">Villas</option>
                        <option value="plots">Plots</option>
                        <option value="hotels">Hotels</option>
                        <option value="projects_offplan">Projects / Off-plan</option>
                      </select>
                    </div>

                    {/* Location */}
                    <div className="flex w-full items-center justify-between rounded-2xl border border-black/5 bg-white px-3 py-2.5 text-xs text-black/70 md:text-sm">
                      <span>Location</span>
                      <input
                        type="text"
                        value={locationQuery}
                        onChange={(e) => setLocationQuery(e.target.value)}
                        placeholder="Dubai, USA, etc."
                        className="ml-3 flex-1 bg-transparent text-right text-xs text-black/80 placeholder:text-black/30 outline-none md:text-sm"
                      />
                    </div>

                    {/* Price range */}
                    <div className="flex w-full items-center justify-between gap-2 rounded-2xl border border-black/5 bg-white px-3 py-2.5 text-xs text-black/70 md:text-sm">
                      <div className="flex flex-1 items-center gap-1">
                        <span className="text-black/50">Min</span>
                        <input
                          type="number"
                          min={0}
                          value={minPrice}
                          onChange={(e) => setMinPrice(e.target.value)}
                          placeholder="AED"
                          className="flex-1 bg-transparent text-right text-xs text-black/80 placeholder:text-black/30 outline-none md:text-sm"
                        />
                      </div>
                      <span className="text-black/40">-</span>
                      <div className="flex flex-1 items-center gap-1">
                        <span className="text-black/50">Max</span>
                        <input
                          type="number"
                          min={0}
                          value={maxPrice}
                          onChange={(e) => setMaxPrice(e.target.value)}
                          placeholder="AED"
                          className="flex-1 bg-transparent text-right text-xs text-black/80 placeholder:text-black/30 outline-none md:text-sm"
                        />
                      </div>
                    </div>

                    {/* Advanced (visual only for now) */}
                    <button className="flex items-center gap-3 rounded-2xl bg-white px-2 py-1.5 text-xs font-medium text-black md:text-sm">
                      <span className="flex h-8 w-8 items-center justify-center rounded-full bg-black/5">
                        <TuneOutlinedIcon className="h-4 w-4 text-black/60" />
                      </span>
                      <span>Advanced</span>
                    </button>

                    {/* Search button */}
                    <MotionButton
                      type="button"
                      onClick={handleSearch}
                      className="flex w-full items-center justify-center gap-2 rounded-2xl bg-black px-4 py-2.5 text-xs font-semibold text-white shadow-md hover:bg-black/90 md:text-sm"
                    >
                      <SearchOutlinedIcon className="h-4 w-4" />
                      <span>Search</span>
                    </MotionButton>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Client logos strip (endless engine-driven) */}
      <ClientLogoStrip />
    </FadeInSection>
  );
};

export default HomeHero;
